import { Box, TextField } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';

import { Page } from '../../components';
import { AdminTable } from '../../components/table/AdminTable';
import { useToastContext } from '../../hooks';
import { IActivityType, ISpecialtyType, SpecialtyTypeFactory } from '../../models';
import { ISpecialtyTypesAPI } from '../../fetch';
import { ConfirmationDialogue } from '../../components/modal/ConfirmationDialogue';

export const EmployeeSpecialtyTypes: FC = () => {
  const { showToast } = useToastContext();

  const [specialtyTypes, setSpecialtyTypes] = useState<ISpecialtyType[]>([]);
  const [isSpecialtyTypes, setIsLoadingSpecialtyTypes] = useState(false);
  const [isSavingSpecialtyTypes, setIsSavingSpecialtyTypes] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [isDeletingSpecialtyType, setIsDeletingSpecialtyType] = useState<false | ISpecialtyType>(
    false
  );

  const removeRow = (row: ISpecialtyType): void => {
    setSpecialtyTypes((state: ISpecialtyType[]) => {
      const index = state.indexOf(row);
      const newState = state.slice();
      // Cut out the deleted row
      newState.splice(index, 1);
      return newState;
    });
  };

  const fetchSpecialtyTypes = useCallback(async () => {
    setIsLoadingSpecialtyTypes(true);
    try {
      setSpecialtyTypes(await ISpecialtyTypesAPI.getAll());
    } catch (err) {
      const message = err instanceof Error && err.message;
      showToast('error', message || 'Failed to fetch specialty types.');
    } finally {
      setIsLoadingSpecialtyTypes(false);
    }
    // Ignore showToast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    fetchSpecialtyTypes();
  }, [fetchSpecialtyTypes]);

  const columns = useMemo(() => {
    return [
      {
        accesor: 'name',
        Header: 'Specialty Type',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: ISpecialtyType; index: number } };
        }) => {
          const [specialtyTypeName, setSpecialtyTypeName] = useState(original.name);

          return (
            <Box>
              <TextField
                fullWidth
                inputProps={{
                  className: 'specialty-type-name-input',
                }}
                autoFocus
                placeholder="Specialty Type Name"
                value={specialtyTypeName}
                onChange={event => setSpecialtyTypeName(event.target.value)}
                onBlur={() => {
                  // Write changes
                  setSpecialtyTypes((state: ISpecialtyType[]) => {
                    if (state[index].name !== specialtyTypeName) {
                      setHasChanges(true);
                    }

                    state[index].name = specialtyTypeName;
                    return state;
                  });
                }}
                onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                  event.target?.select?.();
                }}
              />
            </Box>
          );
        },
      },
      {
        accesor: 'netsuiteSpecialtyTypeId',
        Header: 'NetSuite Specialty Type ID',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IActivityType; index: number } };
        }) => {
          const [netSuiteSpecialtyTypeId, setNetSuiteSpecialtyTypeId] = useState<null | number>(
            original.netSuiteSpecialtyTypeId
          );

          return (
            <Box>
              <NumberFormat
                fullWidth
                isNumericString
                decimalScale={0}
                size="small"
                allowNegative={false}
                customInput={TextField}
                value={netSuiteSpecialtyTypeId}
                onValueChange={values => {
                  const value = parseInt(values.value, 10);
                  if (Number.isNaN(value)) setNetSuiteSpecialtyTypeId(null);
                  setNetSuiteSpecialtyTypeId(value);
                }}
                onBlur={() => {
                  // Write changes
                  setSpecialtyTypes((state: ISpecialtyType[]) => {
                    if (state[index].netSuiteSpecialtyTypeId !== netSuiteSpecialtyTypeId) {
                      setHasChanges(true);
                    }

                    state[index].netSuiteSpecialtyTypeId = netSuiteSpecialtyTypeId;
                    return state;
                  });
                }}
                onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                  event.target?.select?.();
                }}
              />
            </Box>
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialtyTypes]);

  return (
    <Page title="Employee Specialty Types">
      <AdminTable
        data={specialtyTypes}
        columns={columns}
        isLoading={isSpecialtyTypes}
        hasChanges={hasChanges}
        isSaving={isSavingSpecialtyTypes}
        onAdd={() => {
          // Add new empty row
          setSpecialtyTypes(state => state.concat(SpecialtyTypeFactory.getDefault()));
        }}
        onSave={async function upsertActivityTypes() {
          setIsSavingSpecialtyTypes(true);
          try {
            const updatedList: ISpecialtyType[] = await ISpecialtyTypesAPI.upsert(specialtyTypes);
            setSpecialtyTypes(updatedList);
            setHasChanges(false);
            showToast('success', 'Specialty Type was updated successfully!');
          } catch (err) {
            const message =
              err instanceof Error ? err.message : 'Failed to update specialty types.';
            showToast('error', message);
          } finally {
            setIsSavingSpecialtyTypes(false);
          }
        }}
        onDelete={(original, _index) => {
          if (original.specialtyTypeId === SpecialtyTypeFactory.getDefault().specialtyTypeId) {
            removeRow(original);
            showToast('info', 'Removed specialty type.');
          } else {
            setIsDeletingSpecialtyType(original);
          }
        }}
      />
      {/** ConfirmationDialogue */}
      {!!isDeletingSpecialtyType && (
        <ConfirmationDialogue
          open
          id="delete-specialty-type"
          title={`Delete Specialty Type ${isDeletingSpecialtyType.name}`}
          onClose={() => setIsDeletingSpecialtyType(false)}
          onConfirm={async function deleteSpecialtyType() {
            // Check if modal is open and has proper data
            if (!isDeletingSpecialtyType) {
              return;
            }
            // If this is a new, not-yet-saved entry, it does not exist in the DB
            if (
              isDeletingSpecialtyType.specialtyTypeId ===
              SpecialtyTypeFactory.getDefault().specialtyTypeId
            ) {
              // So, deletion only consists of removal from local state
              removeRow(isDeletingSpecialtyType);
            } else {
              // Otherwise this entry already exists, hit the API to delete it
              try {
                await ISpecialtyTypesAPI.delete(isDeletingSpecialtyType.specialtyTypeId);
                // Update state after only successful delete
                removeRow(isDeletingSpecialtyType);
                showToast('success', 'Successfully deleted specialty type.');
              } catch (error) {
                showToast('error', error);
              }
            }

            // Close modal
            setIsDeletingSpecialtyType(false);
          }}
        />
      )}
    </Page>
  );
};
